import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import consultantApi from '../../services/consultantApi';
import api from '../../services/api';
import './CreateJob.css';

const ConsultantEditJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('self');
  const [skillSearch, setSkillSearch] = useState('');
  const [benefitSearch, setBenefitSearch] = useState('');
  const [showCompanyName, setShowCompanyName] = useState(false);

  // Master data
  const [jobTypes, setJobTypes] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [educations, setEducations] = useState([]);
  const [consultantSkills, setConsultantSkills] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [customBenefitInput, setCustomBenefitInput] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    responsibilities: '',
    requirements: '',
    jobType: '',
    workMode: 'onsite',
    location: {
      city: '',
      state: '',
      country: '',
      address: '',
      remote: false
    },
    salary: {
      min: '',
      max: '',
      currency: 'USD',
      period: 'monthly',
      negotiable: false
    },
    experienceRequired: {
      min: '',
      max: ''
    },
    educationRequired: '',
    shiftType: '',
    industryType: '',
    numberOfPositions: 1,
    applicationDeadline: '',
    employmentType: 'full-time',
    requiredSkills: [],
    optionalSkills: [],
    benefits: [],
    customBenefits: [], // For custom text benefits
    postUnderOwnName: true,
    showCompanyName: false,
  });

  useEffect(() => {
    fetchMasterData();
    if (jobId) {
      fetchJobData();
    }
  }, [jobId]);

  const fetchJobData = async () => {
    try {
      setLoading(true);
      const response = await consultantApi.getJobById(jobId);
      const job = response.data;

      if (!job) {
        toast.error('Job not found');
        navigate('/consultant/jobs');
        return;
      }

      // Populate form with job data
      setFormData({
        title: job.title || '',
        description: job.description || '',
        responsibilities: job.responsibilities || '',
        requirements: job.requirements || '',
        jobType: job.jobType?._id || '',
        workMode: job.workMode || 'onsite',
        location: {
          city: job.location?.city || '',
          state: job.location?.state || '',
          country: job.location?.country || '',
          address: job.location?.address || '',
          remote: job.location?.remote || false
        },
        salary: {
          min: job.salary?.min || '',
          max: job.salary?.max || '',
          currency: job.salary?.currency || 'USD',
          period: job.salary?.period || 'monthly',
          negotiable: job.salary?.negotiable || false
        },
        experienceRequired: {
          min: job.experienceRequired?.min || '',
          max: job.experienceRequired?.max || ''
        },
        educationRequired: job.educationRequired?._id || '',
        shiftType: job.shiftType?._id || '',
        industryType: job.industryType?._id || '',
        numberOfPositions: job.numberOfPositions || 1,
        applicationDeadline: job.applicationDeadline ? new Date(job.applicationDeadline).toISOString().split('T')[0] : '',
        employmentType: job.employmentType || 'full-time',
        requiredSkills: job.requiredSkills?.map(s => s._id || s) || [],
        optionalSkills: job.optionalSkills?.map(s => s._id || s) || [],
        benefits: job.benefits?.map(b => b._id || b) || [],
        customBenefits: job.customBenefits || [],
        postUnderOwnName: job.postUnderOwnName !== false,
        showCompanyName: job.showCompanyName || false,
      });

      // Set company selection
      if (job.companyId) {
        setSelectedCompanyId(job.companyId._id);
        setShowCompanyName(job.showCompanyName || false);
      } else {
        setSelectedCompanyId('self');
      }

    } catch (error) {
      console.error('Error fetching job data:', error);
      toast.error('Failed to load job data');
      navigate('/consultant/jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    setDataLoading(true);
    try {
      // Fetch master data (excluding benefits for now)
      // Backend returns: { success: true, data: [...] } via ApiResponse
      // Axios interceptor does response.data, so we get: { success: true, data: [...] }
      const [jobTypesRes, shiftTypesRes, industryTypesRes, educationsRes, consultantSkillsRes] = await Promise.all([
        api.get('/master/job-types'),
        api.get('/master/shift-types'),
        api.get('/master/industry-types'),
        api.get('/master/educations'),
        api.get('/consultant/skills').catch(() => ({ data: [] })) // Get consultant skills from consultants endpoint
      ]);

      // ApiResponse format: { success: true, data: [...] }
      // So res.data is the array we want
      const extract = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        return [];
      };

      setJobTypes(extract(jobTypesRes));
      setShiftTypes(extract(shiftTypesRes));
      setIndustryTypes(extract(industryTypesRes));
      setEducations(extract(educationsRes));
      setConsultantSkills(extract(consultantSkillsRes));

      // Benefits: Try multiple endpoints to get ALL benefits including admin-added ones
      let allBenefits = [];

      try {
        // First try: Get consultant-specific benefits (includes admin + consultant's own)
        const consultantBenefitsRes = await api.get('/consultant/benefits');
        const consultantBenefits = consultantBenefitsRes.data || [];
        // console.log('Consultant benefits loaded:', consultantBenefits.length, 'items');
        allBenefits = [...consultantBenefits];
      } catch (consultantError) {
        // console.log('Consultant benefits endpoint failed:', consultantError.message);
      }

      try {
        // Second try: Get master benefits (admin-created only)
        const masterBenefitsRes = await api.get('/master/benefits');
        const masterBenefits = masterBenefitsRes.data || [];
        // console.log('Master benefits loaded:', masterBenefits.length, 'items');

        // Merge and deduplicate by _id
        const mergedBenefits = [...allBenefits];
        masterBenefits.forEach(masterBenefit => {
          if (!mergedBenefits.find(b => b._id === masterBenefit._id)) {
            mergedBenefits.push(masterBenefit);
          }
        });
        allBenefits = mergedBenefits;
      } catch (masterError) {
        // console.log('Master benefits endpoint failed:', masterError.message);
      }

      // If we have benefits from API, use them
      if (allBenefits.length > 0) {
        setBenefits(allBenefits);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load master data. Please refresh.');
    } finally {
      setDataLoading(false);
    }

    // Fetch companies separately so one failure doesn't block master data
    try {
      const companiesRes = await consultantApi.getMyCompanies();
      // api interceptor already did response.data, so companiesRes = { success, data: { active:[..], pending:[..] } }
      const raw = companiesRes?.data?.data || companiesRes?.data || {};
      const assocList = Array.isArray(raw) ? raw : (raw?.active || []);
      const companyList = assocList
        .filter(assoc => {
          const status = assoc.status || assoc.associationStatus;
          return !status || status === 'accepted' || status === 'active';
        })
        .map(assoc => ({
          _id: assoc.company?._id || assoc.companyId?._id || assoc.companyId,
          name: assoc.company?.name || assoc.companyId?.name || 'Unknown Company',
          industry: assoc.company?.industry || assoc.companyId?.industry,
        }))
        .filter(c => c._id);
      setCompanies(companyList);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleSkillsChange = (e) => {
    const { value, checked } = e.target;
    const skillType = e.target.dataset.type;

    if (skillType === 'required') {
      setFormData({
        ...formData,
        requiredSkills: checked
          ? [...formData.requiredSkills, value]
          : formData.requiredSkills.filter(s => s !== value)
      });
    } else {
      setFormData({
        ...formData,
        optionalSkills: checked
          ? [...formData.optionalSkills, value]
          : formData.optionalSkills.filter(s => s !== value)
      });
    }
  };

  const handleBenefitsChange = (e) => {
    const { value, checked } = e.target;
    setFormData({
      ...formData,
      benefits: checked
        ? [...formData.benefits, value]
        : formData.benefits.filter(b => b !== value)
    });
  };

  const handleAddCustomBenefit = () => {
    const trimmed = customBenefitInput.trim();
    if (trimmed && !formData.customBenefits.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customBenefits: [...prev.customBenefits, trimmed]
      }));
      setCustomBenefitInput('');
    }
  };

  const handleRemoveCustomBenefit = (benefitToRemove) => {
    setFormData(prev => ({
      ...prev,
      customBenefits: prev.customBenefits.filter(b => b !== benefitToRemove)
    }));
  };

  const handleAddBenefit = async () => {
    if (!newBenefit.trim()) return;

    try {
      const res = await api.post('/employer/benefits', {
        name: newBenefit
      });

      const createdBenefit = res.data;

      // Add to benefits list
      setBenefits(prev => [...prev, createdBenefit]);

      // Auto select it
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, createdBenefit._id]
      }));

      setNewBenefit('');
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to add benefit';
      toast.error(errorMessage);
    }
  };

  const handleAddConsultantSkill = async () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;

    try {
      const res = await api.post('/consultant/skills', {
        name: trimmed
      });

      const createdSkill = res.data;

      // Add to consultant skills list
      setConsultantSkills(prev => [...prev, createdSkill]);

      // Auto select it as required skill
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, createdSkill._id]
      }));

      setNewSkill('');
      toast.success('Skill added successfully!');
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to add skill';
      toast.error(errorMessage);
    }
  };

  const handleRemoveConsultantSkill = async (skillId) => {
    try {
      await api.delete(`/consultant/skills/${skillId}`);
      setConsultantSkills(prev => prev.filter(s => s._id !== skillId));

      // Remove from form selection if present
      setFormData(prev => ({
        ...prev,
        requiredSkills: prev.requiredSkills.filter(id => id !== skillId),
        optionalSkills: prev.optionalSkills?.filter(id => id !== skillId) || []
      }));

      toast.success('Skill deleted successfully!');
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to delete skill';
      toast.error(errorMessage);
    }
  };

  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();

    if (!formData.title.trim()) { toast.error('Job title is required'); return; }
    if (!formData.description.trim()) { toast.error('Job description is required'); return; }
    if (!formData.location.city.trim()) { toast.error('City is required'); return; }
    if (!formData.location.country.trim()) { toast.error('Country is required'); return; }
    if (!formData.jobType) { toast.error('Job type is required'); return; }
    if (!formData.workMode) { toast.error('Work mode is required'); return; }

    const chosenCompany = selectedCompanyId !== 'self'
      ? companies.find(c => c._id === selectedCompanyId)
      : null;

    const jobData = {
      ...formData,
      status,
      salary: {
        ...formData.salary,
        min: parseInt(formData.salary.min) || 0,
        max: parseInt(formData.salary.max) || 0
      },
      experienceRequired: {
        min: parseInt(formData.experienceRequired.min) || 0,
        max: parseInt(formData.experienceRequired.max) || 0
      },
      numberOfPositions: parseInt(formData.numberOfPositions) || 1,
      companyId: chosenCompany?._id || null,
      postUnderOwnName: selectedCompanyId === 'self',
      showCompanyName: selectedCompanyId !== 'self' ? showCompanyName : false,
    };

    // Remove empty optional ObjectId fields so MongoDB doesn't reject them
    ['educationRequired', 'shiftType', 'industryType'].forEach(field => {
      if (!jobData[field]) delete jobData[field];
    });
    if (!jobData.companyId) delete jobData.companyId;
    if (!jobData.applicationDeadline) delete jobData.applicationDeadline;

    // Ensure arrays are properly formatted (not strings)
    if (Array.isArray(jobData.requiredSkills) && jobData.requiredSkills.length === 0) {
      delete jobData.requiredSkills;
    }
    if (Array.isArray(jobData.optionalSkills) && jobData.optionalSkills.length === 0) {
      delete jobData.optionalSkills;
    }
    if (Array.isArray(jobData.benefits) && jobData.benefits.length === 0) {
      delete jobData.benefits;
    }

    try {
      setLoading(true);
      const response = await consultantApi.updateJob(jobId, jobData);
      const job = response.data;

      // Show appropriate message based on approval status
      if (job.approvalStatus === 'approved' && job.status === 'active') {
        toast.success('✅ Job updated successfully! It\'s now live and visible to candidates.', {
          duration: 5000,
          icon: '🎉'
        });
      } else {
        toast.success('✅ Job updated successfully! You can send it for approval when ready.', {
          duration: 4000,
          icon: '📝'
        });
      }

      navigate('/consultant/jobs');
    } catch (error) {
      console.error('Error updating job:', error);
      const msg = error?.error?.message || error?.message || 'Failed to update job';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ccj-container">
      <div className="ccj-header">
        <div>
          <h1 className="ccj-title">Edit Job Posting</h1>
          <p className="ccj-subtitle">Post a job on your own behalf or for an associated company</p>
        </div>
        <button onClick={() => navigate('/consultant/jobs')} className="ccj-btn-back">
          <span className="material-symbols-outlined">arrow_back</span>
          Back to Jobs
        </button>
      </div>

      <form onSubmit={handleSubmit} className="ccj-form">
        {/* Post As Section */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">badge</span>
            Post As
          </h2>
          <div className="ccj-info-box">
            <span className="material-symbols-outlined">info</span>
            <p>Choose whether to post under <strong>your own name</strong> or on behalf of an <strong>accepted company</strong>. To add companies, go to the Companies page and send/accept association requests.</p>
          </div>
          <div className="ccj-field">
            <label className="ccj-label">Post As / Company <span>*</span></label>
            <select
              name="postAs"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="ccj-select"
            >
              <option value="self">👤 My Name — {user?.name || 'Consultant'} (Independent)</option>
              {companies.length > 0 && (
                <optgroup label="My Associated Companies">
                  {companies.map(company => (
                    <option key={company._id} value={company._id}>
                      🏢 {company.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {selectedCompanyId === 'self'
              ? <p className="ccj-hint">✔ Posting as independent consultant under your name</p>
              : <p className="ccj-hint ccj-hint-company">✔ Posting on behalf of: <strong>{companies.find(c => c._id === selectedCompanyId)?.name}</strong></p>
            }
            {companies.length === 0 && (
              <p className="ccj-hint">No associated companies yet. You can still post under your own name, or <a href="/consultant/companies" className="ccj-link">add company associations</a>.</p>
            )}

            {selectedCompanyId !== 'self' && companies.length > 0 && (
              <div className="ccj-field">
                <label className="ccj-checkbox-label">
                  <input
                    type="checkbox"
                    checked={showCompanyName}
                    onChange={(e) => setShowCompanyName(e.target.checked)}
                  />
                  <span>Show company name alongside my name?</span>
                </label>
                <p className="ccj-hint">
                  {showCompanyName
                    ? `Job will show: "${user?.name || 'Consultant'} (on behalf of ${companies.find(c => c._id === selectedCompanyId)?.name})"`
                    : `Job will show only: "${user?.name || 'Consultant'}"`
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">work</span>
            Basic Information
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field ccj-field-full">
              <label className="ccj-label">Job Title <span>*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="ccj-input"
                placeholder="e.g., Senior Software Engineer"
                required
              />
            </div>

            <div className="ccj-field ccj-field-full">
              <label className="ccj-label">Job Description <span>*</span></label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="ccj-textarea"
                placeholder="Provide a detailed description of the role..."
                rows="6"
                required
              />
            </div>

            <div className="ccj-field">
              <label className="ccj-label">Job Type <span>*</span></label>
              <select
                name="jobType"
                value={formData.jobType}
                onChange={handleChange}
                className="ccj-select"
                required
              >
                <option value="">Select Job Type</option>
                {jobTypes.map(type => (
                  <option key={type._id} value={type._id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="ccj-field">
              <label className="ccj-label">Work Mode <span>*</span></label>
              <select
                name="workMode"
                value={formData.workMode}
                onChange={handleChange}
                className="ccj-select"
                required
              >
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            {selectedCompanyId === 'self' && (
              <div className="ccj-field">
                <label className="ccj-label">Industry Type</label>
                <select
                  name="industryType"
                  value={formData.industryType}
                  onChange={handleChange}
                  className="ccj-select"
                >
                  <option value="">Select Industry</option>
                  {industryTypes.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="ccj-field">
              <label className="ccj-label">Shift Type</label>
              <select
                name="shiftType"
                value={formData.shiftType}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="">Select Shift Type</option>
                {shiftTypes.map(type => (
                  <option key={type._id} value={type._id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="ccj-field">
              <label className="ccj-label">Number of Positions <span>*</span></label>
              <input
                type="number"
                name="numberOfPositions"
                value={formData.numberOfPositions}
                onChange={handleChange}
                className="ccj-input"
                min="1"
                required
              />
            </div>

            <div className="ccj-field">
              <label className="ccj-label">Application Deadline</label>
              <input
                type="date"
                name="applicationDeadline"
                value={formData.applicationDeadline}
                onChange={handleChange}
                className="ccj-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">location_on</span>
            Location
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field">
              <label className="ccj-label">City <span>*</span></label>
              <input
                type="text"
                name="location.city"
                value={formData.location.city}
                onChange={handleChange}
                className="ccj-input"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">State <span>*</span></label>
              <input
                type="text"
                name="location.state"
                value={formData.location.state}
                onChange={handleChange}
                className="ccj-input"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Country <span>*</span></label>
              <input
                type="text"
                name="location.country"
                value={formData.location.country}
                onChange={handleChange}
                className="ccj-input"
                required
              />
            </div>
            <div className="ccj-field ccj-field">
              <label className="ccj-label">Address <span>*</span></label>
              <input
                type="text"
                name="location.address"
                required
                value={formData.location.address || ''}
                onChange={handleChange}
                className="ccj-input"
                placeholder="Enter complete address"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-checkbox-label">
                <input
                  type="checkbox"
                  name="location.remote"
                  checked={formData.location.remote}
                  onChange={handleChange}
                />
                <span>Remote Work Available</span>
              </label>
            </div>
          </div>
        </div>

        {/* Salary & Experience */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">currency_rupee</span>
            Salary & Experience
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field">
              <label className="ccj-label">Min Salary <span>*</span></label>
              <input
                type="number"
                name="salary.min"
                value={formData.salary.min}
                onChange={handleChange}
                className="ccj-input"
                min="0"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Max Salary <span>*</span></label>
              <input
                type="number"
                name="salary.max"
                value={formData.salary.max}
                onChange={handleChange}
                className="ccj-input"
                min="0"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Currency</label>
              <select
                name="salary.currency"
                value={formData.salary.currency}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="INR">INR</option>
              </select>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Salary Period <span>*</span></label>
              <select
                name="salary.period"
                value={formData.salary.period || 'monthly'}
                onChange={handleChange}
                className="ccj-select"
                required
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="ccj-field-full">
              <label className="ccj-checkbox-label">
                <input
                  type="checkbox"
                  name="salary.negotiable"
                  checked={formData.salary.negotiable}
                  onChange={handleChange}
                />
                <span>Negotiable</span>
              </label>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Min Experience (years) <span>*</span></label>
              <input
                type="number"
                name="experienceRequired.min"
                value={formData.experienceRequired.min}
                onChange={handleChange}
                className="ccj-input"
                min="0"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Max Experience (years) <span>*</span></label>
              <input
                type="number"
                name="experienceRequired.max"
                value={formData.experienceRequired.max}
                onChange={handleChange}
                className="ccj-input"
                min="0"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Education Required</label>
              <select
                name="educationRequired"
                value={formData.educationRequired}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="">Select Education</option>
                {educations.map(edu => (
                  <option key={edu._id} value={edu._id}>{edu.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Responsibilities & Requirements */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">checklist</span>
            Details
          </h2>
          <div className="ccj-field ccj-field-full">
            <label className="ccj-label">Responsibilities <span>*</span></label>
            <textarea
              name="responsibilities"
              value={formData.responsibilities}
              onChange={handleChange}
              className="ccj-textarea"
              placeholder="List key responsibilities..."
              rows="5"
              required
            />
          </div>
          <div className="ccj-field ccj-field-full">
            <label className="ccj-label">Requirements <span>*</span></label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              className="ccj-textarea"
              placeholder="List requirements and qualifications..."
              rows="5"
              required
            />
          </div>
        </div>

        {/* Skills */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">psychology</span>
            Skills
            {(formData.requiredSkills.length > 0 || formData.optionalSkills.length > 0) && (
              <span className="ccj-count-badge">
                {formData.requiredSkills.length + formData.optionalSkills.length} selected
              </span>
            )}
          </h2>

          {/* Skill search */}
          <div className="ccj-search-box">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search skills..."
              value={skillSearch}
              onChange={e => setSkillSearch(e.target.value)}
              className="ccj-search-input"
            />
          </div>

          {dataLoading ? (
            <p className="ccj-loading-hint">Loading skills...</p>
          ) : consultantSkills.length === 0 ? (
            <div className="ccj-empty-skills">
              <p className="ccj-empty-hint">No skills available yet. Create your own skills below!</p>

              {/* Add new skill section */}
              <div className="ccj-add-skill-section">
                <h4>Create New Skill</h4>
                <div className="ccj-skill-input-row">
                  <input
                    type="text"
                    placeholder="Enter skill name..."
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddConsultantSkill()}
                    className="ccj-skill-input"
                  />
                  <button type="button" onClick={handleAddConsultantSkill} className="ccj-add-skill-btn">
                    <span className="material-symbols-outlined">add</span> Add Skill
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Add new skill section for existing skills */}
              <div className="ccj-add-skill-section">
                <h4>Add New Skill</h4>
                <div className="ccj-skill-input-row">
                  <input
                    type="text"
                    placeholder="Enter skill name..."
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddConsultantSkill()}
                    className="ccj-skill-input"
                  />
                  <button type="button" onClick={handleAddConsultantSkill} className="ccj-add-skill-btn">
                    <span className="material-symbols-outlined">add</span> Add Skill
                  </button>
                </div>
              </div>

              <div className="ccj-skills-section">
                <h3>Required Skills
                  {formData.requiredSkills.length > 0 && <span className="ccj-sub-badge">{formData.requiredSkills.length}</span>}
                </h3>
                <div className="ccj-skills-grid">
                  {consultantSkills
                    .filter(s => s.name.toLowerCase().includes(skillSearch.toLowerCase()))
                    .map(skill => (
                      <label
                        key={`req-${skill._id}`}
                        className={`ccj-skill-checkbox ${formData.requiredSkills.includes(skill._id) ? 'ccj-skill-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          value={skill._id}
                          data-type="required"
                          checked={formData.requiredSkills.includes(skill._id)}
                          onChange={handleSkillsChange}
                        />
                        <span>{skill.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveConsultantSkill(skill._id)}
                          className="ccj-skill-delete-btn"
                          title="Delete skill"
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </label>
                    ))}
                </div>
              </div>
              <div className="ccj-skills-section">
                <h3>Optional Skills
                  {formData.optionalSkills.length > 0 && <span className="ccj-sub-badge">{formData.optionalSkills.length}</span>}
                </h3>
                <div className="ccj-skills-grid">
                  {consultantSkills
                    .filter(s => s.name.toLowerCase().includes(skillSearch.toLowerCase()))
                    .map(skill => (
                      <label
                        key={`opt-${skill._id}`}
                        className={`ccj-skill-checkbox ${formData.optionalSkills.includes(skill._id) ? 'ccj-skill-selected ccj-skill-optional' : ''}`}
                      >
                        <input
                          type="checkbox"
                          value={skill._id}
                          data-type="optional"
                          checked={formData.optionalSkills.includes(skill._id)}
                          onChange={handleSkillsChange}
                        />
                        <span>{skill.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveConsultantSkill(skill._id)}
                          className="ccj-skill-delete-btn"
                          title="Delete skill"
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </label>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Benefits */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">card_giftcard</span>
            Benefits
            {formData.benefits.length > 0 && (
              <span className="ccj-count-badge">{formData.benefits.length} selected</span>
            )}
          </h2>

          <div className="ccj-search-box">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search benefits..."
              value={benefitSearch}
              onChange={e => setBenefitSearch(e.target.value)}
              className="ccj-search-input"
            />
          </div>

          {dataLoading ? (
            <p className="ccj-loading-hint">Loading benefits...</p>
          ) : benefits.length === 0 ? (
            <p className="ccj-empty-hint">No benefits available. Add benefits from Admin → Master Data.</p>
          ) : (
            <>
              <div className="ccj-field">
                <label className="ccj-label">Benefits</label>
                <div className="ccj-skills-grid">
                  {benefits.map(b => (
                    <label key={b._id} className={`ccj-skill-checkbox ${formData.benefits.includes(b._id) ? 'ccj-skill-selected ccj-skill-benefit' : ''}`}>
                      <input type="checkbox" value={b._id} checked={formData.benefits.includes(b._id)} onChange={handleBenefitsChange} />
                      {b.name}
                    </label>
                  ))}
                </div>

                {/* Custom Benefits Section */}
                {formData.customBenefits.length > 0 && (
                  <div style={{marginTop: 15}}>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600}}>Custom Benefits:</div>
                    <div className="ccj-skills-grid">
                      {formData.customBenefits.map((customBenefit, index) => (
                        <label key={`custom-${index}`} className="ccj-skill-checkbox ccj-skill-selected ccj-skill-benefit" style={{backgroundColor: '#fffbeb', borderColor: '#fcd34d'}}>
                          <span>{customBenefit}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomBenefit(customBenefit)}
                            style={{
                              marginLeft: 8,
                              background: 'none',
                              border: 'none',
                              color: '#f44336',
                              cursor: 'pointer',
                              fontSize: 16,
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            ×
                          </button>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ccj-benefit-row" style={{marginTop:15}}>
                  <input
                    type="text"
                    placeholder="Add custom benefit..."
                    value={customBenefitInput}
                    onChange={(e) => setCustomBenefitInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomBenefit()}
                    className="ccj-input ccj-benefit-input"
                  />
                  <button type="button" onClick={handleAddCustomBenefit} className="ccj-add-btn">
                    <span className="material-symbols-outlined">add</span> Add
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="ccj-actions">
          <button
            type="button"
            onClick={() => navigate('/consultant/jobs')}
            className="ccj-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ccj-btn-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="ccj-spinner"></span>
                Updating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">save</span>
                Update Job
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsultantEditJob;
